import React from 'react';
import Link from 'next/link';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS, Document } from '@contentful/rich-text-types';
import { sanitizeHTML } from '@loomstateorg/shared-frontend/helpers/domSanitizer';

const HTML_BLOCK_RE = /<(?:div|h[1-6]|ul|ol|table|section|article|header|footer|nav|main|aside)\b[^>]*>/i;

function applyMarks(text: string, marks: { type: string }[]): string {
  let result = text;
  for (const mark of marks) {
    switch (mark.type) {
      case 'bold':
        result = `<strong>${result}</strong>`;
        break;
      case 'italic':
        result = `<em>${result}</em>`;
        break;
      case 'underline':
        result = `<u>${result}</u>`;
        break;
    }
  }
  return result;
}

function buildParagraphHtml(node: any): string {
  return (node.content || [])
    .map((child: any) => {
      if (child.nodeType !== 'text') return '';
      return applyMarks(child.value || '', child.marks || []);
    })
    .join('');
}

function getTableCellText(cell: any): string {
  if (!cell?.content) return '';
  return cell.content
    .filter((child: any) => child.nodeType === 'text')
    .map((child: any) => child.value || '')
    .join('');
}

interface RichTextProps {
  children?: React.ReactNode;
  content: Document;
  className?: string;
  tableHeadColor?: string;
  tableHeadTextColor?: string;
  tableBodyColor?: string;
  tableBodyTextColor?: string;
}

const RichText: React.FC<RichTextProps> = ({
  content,
  className,
  tableHeadColor,
  tableHeadTextColor,
  tableBodyColor,
  tableBodyTextColor,
}) => {
  const paragraphClass = `text-left lg:text-justify ${className}`;

  const headerCellStyle: React.CSSProperties = {};
  if (tableHeadColor) headerCellStyle.backgroundColor = tableHeadColor;
  if (tableHeadTextColor) headerCellStyle.color = tableHeadTextColor;

  const bodyRowStyle: React.CSSProperties = {};
  if (tableBodyColor) bodyRowStyle.backgroundColor = tableBodyColor;
  if (tableBodyTextColor) bodyRowStyle.color = tableBodyTextColor;

  const options = {
    preserveWhitespace: true,
    renderMark: {
      [MARKS.BOLD]: (text: React.ReactNode) => <strong className="font-body">{text}</strong>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
        const joinedText = (node.content || [])
          .filter((c: any) => c.nodeType === 'text')
          .map((c: any) => c.value || '')
          .join('');

        if (!HTML_BLOCK_RE.test(joinedText)) {
          return <p className={paragraphClass}>{children}</p>;
        }

        const rawHtml = buildParagraphHtml(node);
        const htmlStart = rawHtml.search(HTML_BLOCK_RE);

        if (htmlStart > 0) {
          const textPart = rawHtml.substring(0, htmlStart).replace(/\n+$/, '').trim();
          const htmlPart = rawHtml.substring(htmlStart);
          return (
            <>
              {textPart && (
                <p className={paragraphClass} dangerouslySetInnerHTML={{ __html: sanitizeHTML(textPart) }} />
              )}
              <div className="rich-text-content w-full" dangerouslySetInnerHTML={{ __html: sanitizeHTML(htmlPart) }} />
            </>
          );
        }

        return <div className="rich-text-content w-full" dangerouslySetInnerHTML={{ __html: sanitizeHTML(rawHtml) }} />;
      },
      [BLOCKS.HEADING_1]: (node: any, children: React.ReactNode) => (
        <h1 className="mb-24 text-4xl font-bold">{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (node: any, children: React.ReactNode) => (
        <h2 className="mb-20 text-3xl font-bold">{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node: any, children: React.ReactNode) => (
        <h3 className="mb-16 text-2xl font-bold">{children}</h3>
      ),
      [BLOCKS.UL_LIST]: (node: any, children: React.ReactNode) => <ul className="mb-24 list-disc pl-24">{children}</ul>,
      [BLOCKS.OL_LIST]: (node: any, children: React.ReactNode) => (
        <ol className="mb-24 list-decimal pl-24">{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (node: any, children: React.ReactNode) => <li className="mb-8">{children}</li>,
      [BLOCKS.TABLE]: (node: any, children: React.ReactNode) => {
        const childArray = React.Children.toArray(children);
        const headerRows = childArray.filter((child: any) => child?.type === 'thead');
        const bodyRows = childArray.filter((child: any) => child?.type !== 'thead');

        const rows = node.content || [];
        const headerRowNode = rows.find((row: any) =>
          row.content?.some((cell: any) => cell.nodeType === 'table-header-cell'),
        );
        const bodyRowNodes = rows.filter(
          (row: any) => !row.content?.some((cell: any) => cell.nodeType === 'table-header-cell'),
        );

        const headerCells = headerRowNode?.content || [];
        const columnCount = headerCells.length || rows[0]?.content?.length || 1;
        const sizeColumnCount = Math.max(columnCount - 1, 1);
        const measureHeader = getTableCellText(headerCells[0]) || 'Point of measure';
        const sizeHeaders = headerCells.slice(1);

        const headerStyle = Object.keys(headerCellStyle).length > 0 ? headerCellStyle : undefined;
        const bodyStyle = Object.keys(bodyRowStyle).length > 0 ? bodyRowStyle : undefined;

        return (
          <div className="mb-24 w-full min-w-0">
            <div className="md:hidden">
              <div className="border-b border-black py-8 font-primaryBold text-sm" style={headerStyle}>
                {measureHeader}
              </div>
              <div className="divide-y divide-gray-200">
                {bodyRowNodes.map((row: any, rowIndex: number) => {
                  const cells = row.content || [];
                  const measureLabel = getTableCellText(cells[0]);
                  return (
                    <div key={rowIndex} className="py-12" style={bodyStyle}>
                      <p
                        className="mb-10 font-primaryBold leading-snug text-secondary-regular"
                        style={tableBodyTextColor ? { color: tableBodyTextColor } : undefined}
                      >
                        {measureLabel}
                      </p>
                      <dl>
                        {sizeHeaders.map((sizeCell: any, sizeIndex: number) => {
                          const sizeLabel = getTableCellText(sizeCell);
                          const sizeValue = getTableCellText(cells[sizeIndex + 1]);
                          return (
                            <div
                              key={sizeIndex}
                              className="grid grid-cols-[64px_1fr] items-center gap-x-24 border-b border-gray-200 py-10 last:border-b-0"
                            >
                              <dt className="font-primaryBold text-sm">{sizeLabel}</dt>
                              <dd className="pl-8 text-left font-primaryThin leading-snug">{sizeValue}</dd>
                            </div>
                          );
                        })}
                      </dl>
                    </div>
                  );
                })}
              </div>
            </div>

            <table className="hidden w-full table-fixed border-collapse md:table [&_td:first-child]:whitespace-normal [&_td:first-child]:break-words [&_td:first-child]:py-8 [&_td:first-child]:pr-8 [&_td:first-child]:text-left [&_td:first-child]:align-middle [&_td:first-child]:leading-snug [&_td:not(:first-child)]:whitespace-nowrap [&_td:not(:first-child)]:px-2 [&_td:not(:first-child)]:py-8 [&_td:not(:first-child)]:text-center [&_td:not(:first-child)]:align-middle [&_td:not(:first-child)]:leading-snug [&_th:first-child]:whitespace-normal [&_th:first-child]:break-words [&_th:first-child]:py-8 [&_th:first-child]:pr-8 [&_th:first-child]:text-left [&_th:first-child]:align-middle [&_th:first-child]:leading-snug [&_th:not(:first-child)]:whitespace-nowrap [&_th:not(:first-child)]:px-4 [&_th:not(:first-child)]:py-8 [&_th:not(:first-child)]:text-center [&_th:not(:first-child)]:align-middle [&_th:not(:first-child)]:leading-snug">
              <colgroup>
                <col style={{ width: '38%' }} />
                {Array.from({ length: sizeColumnCount }).map((_, colIndex) => (
                  <col key={`size-col-${colIndex}`} />
                ))}
              </colgroup>
              {headerRows}
              {bodyRows.length > 0 && <tbody className="divide-y divide-gray-200">{bodyRows}</tbody>}
            </table>
          </div>
        );
      },
      [BLOCKS.TABLE_ROW]: (node: any, children: React.ReactNode) => {
        const isHeaderRow = node.content.some((cell: any) => cell.nodeType === 'table-header-cell');
        if (isHeaderRow) {
          return (
            <thead>
              <tr
                className="border-b border-black text-left"
                style={Object.keys(headerCellStyle).length > 0 ? headerCellStyle : undefined}
              >
                {children}
              </tr>
            </thead>
          );
        }
        return <tr style={Object.keys(bodyRowStyle).length > 0 ? bodyRowStyle : undefined}>{children}</tr>;
      },
      [BLOCKS.TABLE_CELL]: (node: any, children: React.ReactNode) => <td className="font-primaryThin">{children}</td>,
      [BLOCKS.TABLE_HEADER_CELL]: (node: any, children: React.ReactNode) => (
        <th className="font-primaryBold text-sm">{children}</th>
      ),
      [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => {
        const uri = node.data.uri;

        if (uri.startsWith('http') || uri.startsWith('mailto:') || uri.startsWith('tel:')) {
          return (
            // testid-ignore: CMS inline link; tests find it by text inside the rich-text container
            <a
              href={uri}
              className="text-primary hover:text-primary/80 underline underline-offset-4"
              target="_blank"
              rel="noreferrer"
            >
              {children}
            </a>
          );
        }

        return (
          // testid-ignore: CMS inline link; tests find it by text inside the rich-text container
          <Link href={uri} className="text-primary hover:text-primary/80 underline underline-offset-4">
            {children}
          </Link>
        );
      },
    },
  };

  return <div className={`w-full ${className}`} data-testid="atom-contentful-rich-text">{documentToReactComponents(content, options)}</div>;
};

export default RichText;
